import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface PaginationProps {
  total: number
  limit: number
  offset: number
  onPageChange: (offset: number) => void
  onLimitChange?: (limit: number) => void
  pageSizeOptions?: number[]
}

export function Pagination({
  total,
  limit,
  offset,
  onPageChange,
  onLimitChange,
  pageSizeOptions = [10, 25, 50, 100],
}: PaginationProps) {
  const currentPage = Math.floor(offset / limit) + 1
  const totalPages = Math.ceil(total / limit)

  const handlePreviousPage = () => {
    if (offset > 0) {
      onPageChange(Math.max(0, offset - limit))
    }
  }

  const handleNextPage = () => {
    if (offset + limit < total) {
      onPageChange(offset + limit)
    }
  }

  const handlePageSizeChange = (value: string) => {
    if (onLimitChange) {
      onLimitChange(parseInt(value, 10))
    }
  }

  const startItem = total === 0 ? 0 : offset + 1
  const endItem = Math.min(offset + limit, total)

  return (
    <div className="flex flex-col gap-2 px-2 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <span className="hidden sm:inline">
          Showing {startItem} to {endItem} of {total} results
        </span>
        {onLimitChange && (
          <div className="flex items-center gap-2">
            <span>per page:</span>
            <Select
              value={String(limit)}
              onValueChange={handlePageSizeChange}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={String(limit)} />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="flex items-center justify-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePreviousPage}
          disabled={offset === 0}
        >
          <ChevronLeft className="h-4 w-4" />
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">
          <span className="sm:hidden">{currentPage}/{totalPages || 1}</span>
          <span className="hidden sm:inline">Page {currentPage} of {totalPages || 1}</span>
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNextPage}
          disabled={offset + limit >= total}
        >
          Next
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
